import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import securityService from './security';
import RefreshTokenInterceptor from './refreshTokenInterceptor';
import { TokenService } from './tokenService';

const api: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  // baseURL: 'https://onlinekrpica-production.up.railway.app',
  withCredentials: true, // Important for CSRF protection with cookies
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

console.log('API baseURL:', process.env.VUE_APP_API_URL);

// Request interceptor for adding auth header and CSRF token
api.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Add authentication token if available
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for mutation operations (POST, PUT, DELETE, PATCH)
    if (
      config.method &&
      ['post', 'put', 'delete', 'patch'].includes(config.method)
    ) {
      // Await the CSRF token - this is key!
      const csrfToken = await securityService.getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Sanitize request data to prevent XSS
    if (
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof FormData)
    ) {
      config.data = securityService.sanitizeObject(config.data);
    }

    // Ne postavljamo Content-Type header ako je FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // If response contains a new CSRF token, update it
    const newCsrfToken = response.headers['x-csrf-token'];
    if (newCsrfToken) {
      securityService.refreshCsrfToken();
    }
    return response;
  },
  (error: AxiosError): Promise<never> => {
    // Global error handling
    if (error.response) {
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - token expired or invalid
          localStorage.removeItem('token');
          // Optional: Redirect to login
          // window.location.href = '/login'
          break;
        case 403:
          // Forbidden - possible CSRF attack or insufficient permissions
          console.error('Access forbidden. Possible CSRF token mismatch.');
          // Refresh CSRF token
          securityService.refreshCsrfToken();
          break;
        case 429:
          // Too Many Requests - rate limited
          console.error('Rate limited. Please try again later.');
          break;
      }
    }
    return Promise.reject(error);
  }
);

// Funkcija za slanje FormData zahteva
export const postFormData = async (
  url: string,
  formData: FormData
): Promise<AxiosResponse> => {
  return api.post(url, formData);
};

// Provera dostupnosti korisničkog imena
export const checkUsernameAvailability = async (
  username: string
): Promise<boolean> => {
  try {
    const response = await api.get(
      `/api/auth/check-username?username=${encodeURIComponent(username)}`
    );
    if (response.data && response.data.status === 'success') {
      return response.data.data.available === true;
    }
    // U slučaju da nije uspešan odgovor, vraćamo true kako ne bismo blokirali korisnika
    return true;
  } catch (error) {
    console.error('Greška pri proveri korisničkog imena:', error);
    // U slučaju greške, pretpostavljamo da je korisničko ime dostupno
    return true;
  }
};

// Provera dostupnosti email adrese
export const checkEmailAvailability = async (
  email: string
): Promise<boolean> => {
  try {
    const response = await api.get(
      `/api/auth/check-email?email=${encodeURIComponent(email)}`
    );
    if (response.data && response.data.status === 'success') {
      return response.data.data.available === true;
    }
    // U slučaju da nije uspešan odgovor, vraćamo true kako ne bismo blokirali korisnika
    return true;
  } catch (error) {
    console.error('Greška pri proveri email adrese:', error);
    // U slučaju greške, pretpostavljamo da je email dostupan
    return true;
  }
};

// Opciono, možete i dobiti poruku ako je dostupna
export const getEmailAvailabilityMessage = async (
  email: string
): Promise<string | null> => {
  try {
    const response = await api.get(
      `/api/auth/check-email?email=${encodeURIComponent(email)}`
    );
    if (
      response.data &&
      response.data.status === 'success' &&
      response.data.data.message
    ) {
      return response.data.data.message;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getUsernameAvailabilityMessage = async (
  username: string
): Promise<string | null> => {
  try {
    const response = await api.get(
      `/api/auth/check-username?username=${encodeURIComponent(username)}`
    );
    if (
      response.data &&
      response.data.status === 'success' &&
      response.data.data.message
    ) {
      return response.data.data.message;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const putFormData = async (
  url: string,
  formData: FormData
): Promise<AxiosResponse> => {
  return api.put(url, formData);
};

export interface NotificationResponse {
  notifications: any[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    hasMore: boolean;
  };
}

export const authService = {
  async verifyEmail(token: string) {
    return api.post('/api/auth/verify-email', { token });
  },

  async resendVerificationEmail(email: string) {
    return api.post('/api/auth/resend-verification', { email });
  },

  async forgotPassword(email: string) {
    return api.post('/api/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string) {
    return api.post('/api/auth/reset-password', { token, password });
  },
};

// API pozivi za notifikacije
export const notificationApi = {
  // Učitaj neisporučene notifikacije (kad se korisnik uloguje)
  async getUndeliveredNotifications() {
    console.log('[NotificationAPI] Getting undelivered notifications...');
    const response = await api.get('/api/notifications/undelivered');
    console.log(
      '[NotificationAPI] Undelivered notifications response:',
      response.data
    );
    return response.data;
  },

  // Učitaj sve notifikacije sa paginacijom
  async getUserNotifications(
    page = 1,
    limit = 20
  ): Promise<NotificationResponse> {
    console.log(
      `[NotificationAPI] Getting user notifications, page ${page}, limit ${limit}`
    );
    const response = await api.get(
      `/api/notifications?page=${page}&limit=${limit}`
    );
    console.log(
      '[NotificationAPI] User notifications response:',
      response.data
    );
    return response.data;
  },

  // Broj nepročitanih
  async getUnreadCount() {
    console.log('[NotificationAPI] Getting unread count...');
    const response = await api.get('/api/notifications/unread-count');
    console.log('[NotificationAPI] Unread count response:', response.data);
    return response.data;
  },

  // Označi kao pročitano
  async markAsRead(notificationId: string) {
    console.log(
      `[NotificationAPI] Marking notification ${notificationId} as read...`
    );
    const response = await api.put(`/api/notifications/${notificationId}/read`);
    console.log('[NotificationAPI] Mark as read response:', response.data);
    return response.data;
  },

  // Označi sve kao pročitano
  async markAllAsRead() {
    console.log('[NotificationAPI] Marking all notifications as read...');
    const response = await api.put('/api/notifications/mark-all-read');
    console.log('[NotificationAPI] Mark all as read response:', response.data);
    return response.data;
  },
};

export default api;

let refreshTokenInterceptor: RefreshTokenInterceptor;

export const initializeInterceptors = (store: any, router: any) => {
  refreshTokenInterceptor = new RefreshTokenInterceptor(api, store, router);
};

export { refreshTokenInterceptor };
